/* codex-run.jsx — the running-build dash for CODEX (agentic) builds.
   Ported verbatim from Viper's Goose-19 handoff (the global-component pattern:
   reads the window-global React + I; registers window.CodexRunPanel + window.CODEX_LOG).

   Non-swarm builds have NO 6-stage rail; Codex runs an agentic loop and emits a
   `codex exec --json` event stream (thread.started · item.completed{agent_message
   | command_execution | file_change} · turn.completed.usage). This panel renders
   that stream live: a build→render→critique→fix phase stepper + ticking usage.
   The scrolling activity feed flows through the existing LiveLog (CODEX_LOG).

   window.CodexRunPanel({ job, running }) is used by JobDetail's Process card when
   job.engine === 'codex'. The STREAM / CODEX_LOG demo data drive the offline mock
   preview; the real wiring tails _codex/stream-N.jsonl into both (supervisor, PY-05). */

(function () {
  const { useState, useEffect, useRef } = React;

  // phases of the orchestrated agentic loop (the stepper)
  const PHASES = [
    { id: 'build', label: 'Build', ico: 'code' },
    { id: 'render', label: 'Render', ico: 'image' },
    { id: 'critique', label: 'Critique', ico: 'review' },
    { id: 'fix', label: 'Fix', ico: 'retry' },
  ];

  // the event stream (mirrors a real codex --json run, condensed)
  const STREAM = [
    { ph: 0, k: 'msg', t: "I’m codex. Loading the email inputs, then building index.html against the 2× slices + fidelity spec." },
    { ph: 0, k: 'cmd', t: "sed -n '1,260p' CONVENTIONS.md", ok: true },
    { ph: 0, k: 'cmd', t: "sed -n '1,260p' FIDELITY-SPEC.md", ok: true },
    { ph: 0, k: 'msg', t: "Reading the reference exemplar, design-spec and asset manifest." },
    { ph: 0, k: 'cmd', t: "cat asset-manifest.json design_texts.txt", ok: true },
    { ph: 0, k: 'file', t: "index.html", op: 'add', d: "built 8 bands top-to-bottom" },
    { ph: 1, k: 'msg', t: "Rendering the build to self-check against the slices." },
    { ph: 1, k: 'cmd', t: "node render_html.mjs → _codex/render-2.png", ok: true },
    { ph: 2, k: 'msg', t: "Band 4 rate-table is ~12px narrow vs the design; band-5 background inverted. Fixing both." },
    { ph: 3, k: 'file', t: "index.html", op: 'update', d: "rate-table width + band-5 bg" },
    { ph: 3, k: 'done', t: "WIDTH: OK · bands match — handing index.html to the dev-grounded QA gate." },
  ];

  const EV_ICO = { msg: 'bolt', cmd: 'terminal', file: 'doc', done: 'check' };

  // Derive the REAL running state from the live SSE events (events.ndjson on /api/events/:jobId).
  // NO timer — the panel now tracks the actual build. The supervisor emits `log` events incl. the
  // pass markers ("🤖 codex pass 1/2", "🤖 codex resume pass 2/2"), and (once the live tail lands,
  // PY-05 Step 2) per-file writes + per-turn usage. The real loop is 2 codex passes, mapped onto the
  // 4 orbs: pass 1 = Build (the long first pass), pass 2 (resume) = render→critique→fix (advance to
  // Fix once a rewrite/file-update lands). When no live data exists (mock job / offline), it idles at
  // Build rather than faking a 12s storyboard — honest > pretty.
  function deriveCodex(events, running) {
    let pass = 0, files = 0, usage = null, ended = false, sawRewrite = false;
    for (const ev of (events || [])) {
      if (!ev) continue;
      if (ev.type === 'run_end') ended = true;
      if (ev.type === 'usage' && ev.usage) usage = ev.usage;           // live-tail usage event (Step 2/3)
      if (ev.type === 'log' && ev.msg) {
        const m = String(ev.msg);
        const pm = m.match(/pass\s+(\d+)\s*\/\s*\d+/i);                 // "codex pass 1/2" / "resume pass 2/2"
        if (pm) pass = Math.max(pass, parseInt(pm[1], 10));
        if (/\bfile\b/i.test(m) && /\b(wrote|updated|add|update)\b/i.test(m)) { files++; if (/updat/i.test(m)) sawRewrite = true; }
        const um = m.match(/in[=:\s]+(\d+)\s*k\b[\s\S]*?out[=:\s]+(\d+)\s*k\b/i);
        if (um) usage = { input_tokens: parseInt(um[1], 10) * 1000, output_tokens: parseInt(um[2], 10) * 1000 };
      }
    }
    const done = ended || !running;
    let phase = 0;                                                      // pass 1 = Build (the orb stays here for the real ~14min)
    if (!done && pass >= 2) phase = sawRewrite ? 3 : 1;                 // resume: Render → (Fix once the rewrite lands)
    return { pass: Math.max(pass, 1), files, usage, done, phase };
  }

  function CodexRunPanel({ job, running, events }) {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const live = deriveCodex(events, running);
    const done = live.done || reduce;                                  // not running / run_end → all orbs done
    const curPhase = live.phase;
    const passNo = live.pass;
    // tokens: real per-turn usage (live tail) → the DB-projected total (job.tokensK, refreshed by the
    // 5s detail poll) → em-dash. NEVER a Math.random climb.
    const inK = (live.usage && live.usage.input_tokens != null) ? Math.round(live.usage.input_tokens / 1000)
              : ((job && job.tokensK) || null);
    const outK = (live.usage && live.usage.output_tokens != null) ? Math.round(live.usage.output_tokens / 1000) : null;
    const model = (job && job.engine === 'claude') ? 'claude-cli'
              : ((live.usage && live.usage.model) || (job && job.model) || 'codex');   // CFG-01: live model when present — never hardcode a model name

    return (
      <div className="cx-run">
        <div className="cx-head">
          <div className="cx-steps">
            {PHASES.map((p, i) => {
              const Ico = I[p.ico] || I.code;
              const state = done ? 'done' : i < curPhase ? 'done' : i === curPhase ? 'on' : 'todo';
              return (
                <React.Fragment key={p.id}>
                  {i > 0 && <span className={'cx-conn' + (i <= curPhase || done ? ' lit' : '')}></span>}
                  <div className={'cx-step ' + state} title={p.label}>
                    <span className="cx-orb">{state === 'on' ? <span className="cx-spin"></span> : state === 'done' ? <I.check size={14} /> : <Ico size={14} />}</span>
                    <span className="cx-step-lbl">{p.label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          <div className="cx-meta">
            <span className="cx-model">{model}</span>
            <span className="cx-pass">{done ? 'done' : 'pass ' + passNo + '/2'}</span>
          </div>
        </div>

        <div className="cx-foot">
          <div className="cx-fig"><b className="mono">{inK == null ? '—' : inK + 'K'}</b><span>input · cached</span></div>
          <div className="cx-fig"><b className="mono">{outK == null ? '—' : outK + 'K'}</b><span>output</span></div>
          <div className="cx-fig"><b className="mono">{live.files || 0}</b><span>file writes</span></div>
          <span className="grow"></span>
          <span className="cx-loop">orchestrated render loop · cap 2 passes</span>
        </div>
      </div>
    );
  }

  window.CodexRunPanel = CodexRunPanel;

  // the codex --json stream, mapped to the existing Live-activity log format
  // ({t, c, m}). LiveLog renders this for codex builds instead of the swarm log.
  window.CODEX_LOG = [
    { t: '00:00', c: 'info', m: 'thread 019efd75 · codex · pass 1 of 2' },   // CFG-01: demo seed de-modeled
    { t: '00:01', c: '', m: 'agent · loading email inputs; building index.html against the 2× slices' },
    { t: '00:03', c: '', m: '$ sed CONVENTIONS.md FIDELITY-SPEC.md  ✓' },
    { t: '00:06', c: '', m: '$ cat asset-manifest.json design_texts.txt  ✓' },
    { t: '00:11', c: 'ok', m: 'file · wrote index.html — 8 bands top-to-bottom' },
    { t: '00:13', c: 'info', m: 'pass 2 · render → critique → fix' },
    { t: '00:14', c: '', m: '$ node render_html.mjs → _codex/render-2.png  ✓' },
    { t: '00:18', c: '', m: 'agent · band-4 rate-table ~12px narrow; band-5 bg inverted — fixing' },
    { t: '00:22', c: 'ok', m: 'file · updated index.html — rate-table width + band-5 bg' },
    { t: '00:24', c: 'ok', m: 'WIDTH: OK · bands match → handing to the dev-grounded QA gate' },
  ];
})();
